import type { OperationVariables } from '@apollo/client'
import { skipToken, useSubscription as useBaseSubscription } from '@apollo/client/react/compiled'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'

export type UseSubscriptionResult<TData> = useBaseSubscription.Result<TData>

// `skip` is our own option, not Apollo's: Apollo 4.3 deprecates its `skip` in
// favour of passing `skipToken` instead of the options. Translating here keeps
// every call site on a plain boolean.
type BaseOpts<TData, TVariables extends OperationVariables> = useBaseSubscription.Options<
  NoInfer<TData>,
  NoInfer<TVariables>
>

type UseSubscriptionOpts<TData, TVariables extends OperationVariables> = Omit<
  useBaseSubscription.Base.Options<NoInfer<TData>, NoInfer<TVariables>>,
  'skip'
> &
  Pick<BaseOpts<TData, TVariables>, 'variables'> & { skip?: boolean }

export function useSubscription<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  { skip, ...opts }: UseSubscriptionOpts<TData, TVariables>,
): UseSubscriptionResult<TData> {
  return useBaseSubscription<TData, TVariables>(
    document,
    skip ? skipToken : (opts as BaseOpts<TData, TVariables>),
  ) as UseSubscriptionResult<TData>
}
